import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { ConversationMediaItem, ConversationMediaType } from '@troia-v3/types';

import { Input } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

import ImageViewer from '../../ImageViewer';
import { useConversationMedia } from '../../../hooks';
import { downloadMediaWithFilename } from '../../../utils/downloadMedia';
import { formatFileSize, formatShortDate } from '../detailsFormat';
import type { DetailsData } from '../useDetailsData';

interface FilesLayerProps {
  data: DetailsData;
  conversationId: string;
}

type FilesTab = 'all' | ConversationMediaType;

const ABAS: ReadonlyArray<{ id: FilesTab; label: string; icone?: IconName }> = [
  { id: 'all', label: 'Tudo' },
  { id: 'image', label: 'Imagens', icone: 'image' },
  { id: 'video', label: 'Vídeos', icone: 'video' },
  { id: 'document', label: 'Documentos', icone: 'file-text' },
  { id: 'audio', label: 'Áudios', icone: 'mic' },
];

const ICONE_POR_TIPO: Record<ConversationMediaType, IconName> = {
  image: 'image',
  video: 'video',
  document: 'file-text',
  // Áudio de conversa é recado de voz, não faixa de música: microfone.
  audio: 'mic',
};

const ROTULO_POR_TIPO: Record<ConversationMediaType, string> = {
  image: 'Imagem',
  video: 'Vídeo',
  document: 'Documento',
  audio: 'Áudio',
};

/** Imagem e vídeo aparecem em grade de miniaturas; o resto, em lista. */
const EM_GRADE: ReadonlySet<FilesTab> = new Set<FilesTab>(['image', 'video']);

const contagemDaAba = (aba: FilesTab, counts: DetailsData['mediaCounts']): number =>
  aba === 'all' ? counts.total : counts[aba];

/** Nome exibido: o do arquivo; sem ele, a legenda; sem ela, o tipo. */
const nomeDoArquivo = (item: ConversationMediaItem): string =>
  item.filename ?? item.caption ?? ROTULO_POR_TIPO[item.type];

/**
 * Nome com que o arquivo é salvo. Recado de voz costuma chegar sem `filename`,
 * e salvar como "Áudio" (sem extensão) deixa o arquivo sem programa que abra —
 * por isso o último trecho do caminho da URL vale mais que o rótulo do tipo.
 */
const nomeParaBaixar = (item: ConversationMediaItem): string => {
  if (item.filename) return item.filename;
  try {
    const { pathname } = new URL(item.url, window.location.origin);
    const ultimo = decodeURIComponent(pathname.substring(pathname.lastIndexOf('/') + 1));
    if (ultimo.includes('.')) return ultimo;
  } catch {
    // URL inválida: cai no rótulo abaixo.
  }
  return nomeDoArquivo(item);
};

/** Busca sem acento e sem caixa: quem digita "contrato" acha "Contrato". */
const normalizar = (texto: string): string =>
  texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const descricaoDoItem = (item: ConversationMediaItem): string =>
  `${ROTULO_POR_TIPO[item.type]} · ${formatFileSize(item.size)} · ${formatShortDate(item.sentAt)}`;

const ESTILO_NOME: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.3,
  color: 'var(--theme-card-text)',
};

const ESTILO_META: React.CSSProperties = {
  fontSize: 11,
  lineHeight: 1.3,
  marginTop: 1,
  color: 'var(--theme-muted-text)',
};

/**
 * Camada ARQUIVOS do painel de detalhes.
 *
 * As contagens das abas vêm de `data.mediaCounts` — são GLOBAIS e chegam iguais
 * em toda página, então trocar de aba não muda os números. Já a lista vem da
 * consulta paginada por tipo: o servidor filtra, não o cliente. A busca por
 * nome, essa sim, filtra só o que já foi carregado — é um atalho visual dentro
 * da lista aberta, não uma busca no servidor.
 */
const FilesLayer: React.FC<FilesLayerProps> = ({ data, conversationId }) => {
  const [aba, setAba] = useState<FilesTab>('all');
  const [busca, setBusca] = useState('');
  const [imagemAberta, setImagemAberta] = useState<number | null>(null);

  const {
    data: paginas,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    // A aba "Tudo" não manda tipo nenhum: quem filtra é o servidor, e sem
    // `type` a rota devolve todos os tipos.
  } = useConversationMedia(conversationId, aba === 'all' ? undefined : aba);


  const itens = useMemo<ConversationMediaItem[]>(
    () => (paginas?.pages ?? []).flatMap((pagina) => pagina.items),
    [paginas],
  );

  const termo = normalizar(busca.trim());
  const visiveis = useMemo<ConversationMediaItem[]>(
    () =>
      termo === ''
        ? itens
        : itens.filter((item) => normalizar(nomeDoArquivo(item)).includes(termo)),
    [itens, termo],
  );

  // O visualizador de imagens navega entre as imagens com as setas, então
  // precisa da lista SÓ de imagens — e cada linha precisa saber sua posição
  // dentro dela, que não é a mesma posição da lista misturada da aba "Tudo".
  const { imagens, posicaoDaImagem } = useMemo(() => {
    const somenteImagens = visiveis.filter((item) => item.type === 'image');
    const posicoes = new Map<ConversationMediaItem, number>();
    somenteImagens.forEach((item, i) => posicoes.set(item, i));
    return { imagens: somenteImagens, posicaoDaImagem: posicoes };
  }, [visiveis]);

  const baixar = useCallback(async (item: ConversationMediaItem) => {
    try {
      await downloadMediaWithFilename(item.url, nomeParaBaixar(item));
    } catch {
      toast.error('Não foi possível baixar o arquivo.');
    }
  }, []);

  const sentinelaRef = useRef<HTMLDivElement | null>(null);

  const carregarMais = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // A sentinela só existe quando há próxima página E não há busca em curso. Com
  // busca, ela ficaria visível para sempre (a lista filtrada não a empurra para
  // baixo) e puxaria a galeria inteira, página após página, sem ninguém pedir.
  const mostrarSentinela = hasNextPage === true && termo === '';

  // O observador é recriado a cada mudança de estado da paginação de propósito:
  // quando a página nova chega, a sentinela pode continuar em tela sem gerar um
  // novo cruzamento — e a rolagem infinita travaria com a lista pela metade.
  useEffect(() => {
    const alvo = sentinelaRef.current;
    if (!alvo) return;

    const observador = new IntersectionObserver((entradas) => {
      if (entradas.some((entrada) => entrada.isIntersecting)) carregarMais();
    });
    observador.observe(alvo);

    return () => observador.disconnect();
  }, [carregarMais, mostrarSentinela, visiveis.length]);

  const botaoBaixar = (item: ConversationMediaItem, indice: number): React.ReactNode => (
    <button
      type="button"
      onClick={() => void baixar(item)}
      title="Baixar"
      aria-label={`Baixar ${nomeDoArquivo(item)}`}
      className="cdp-iconbtn"
      data-testid={`details-file-download-${indice}`}
    >
      <Icon name="download" className="w-4 h-4" />
    </button>
  );

  /** Abre imagem no visualizador; os outros tipos, em aba nova. */
  const abridor = (
    item: ConversationMediaItem,
    conteudo: React.ReactNode,
    className: string,
    estilo?: React.CSSProperties,
  ): React.ReactNode => {
    const nome = nomeDoArquivo(item);
    if (item.type === 'image') {
      const posicao = posicaoDaImagem.get(item);
      return (
        <button
          type="button"
          className={className}
          style={estilo}
          aria-label={`Abrir ${nome}`}
          onClick={() => setImagemAberta(posicao ?? 0)}
        >
          {conteudo}
        </button>
      );
    }
    return (
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        style={estilo}
        aria-label={`Abrir ${nome}`}
      >
        {conteudo}
      </a>
    );
  };

  const linha = (item: ConversationMediaItem, indice: number): React.ReactNode => (
    <div
      key={`${item.messageId}-${indice}`}
      className="cdp-line"
      data-testid={`details-file-${indice}`}
    >
      {abridor(
        item,
        <>
          <span className="cdp-lineic">
            <Icon name={ICONE_POR_TIPO[item.type]} className="w-4 h-4" />
          </span>
          <span className="flex-1 min-w-0 text-left">
            <span className="block truncate" style={ESTILO_NOME}>
              {nomeDoArquivo(item)}
            </span>
            <span className="block truncate" style={ESTILO_META}>
              {descricaoDoItem(item)}
            </span>
          </span>
        </>,
        'flex items-center gap-[9px] flex-1 min-w-0',
      )}
      {botaoBaixar(item, indice)}
    </div>
  );

  const celula = (item: ConversationMediaItem, indice: number): React.ReactNode => {
    const miniatura = item.thumbnailUrl ?? (item.type === 'image' ? item.url : undefined);
    return (
      <div
        key={`${item.messageId}-${indice}`}
        data-testid={`details-file-${indice}`}
        style={{
          border: '1px solid var(--theme-sidebar-border)',
          borderRadius: 10,
          overflow: 'hidden',
          background: 'var(--theme-surface-sunken)',
        }}
      >
        {abridor(
          item,
          miniatura ? (
            <img
              src={miniatura}
              alt={nomeDoArquivo(item)}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <Icon name={item.type === 'video' ? 'play' : ICONE_POR_TIPO[item.type]} className="w-6 h-6" />
          ),
          'flex items-center justify-center w-full overflow-hidden',
          // altura fixa: sem ela a miniatura colapsa e a grade vira uma fileira
          // de legendas sem imagem.
          { height: 76, color: 'var(--theme-muted-text)' },
        )}

        <div className="flex items-center gap-1" style={{ padding: '5px 5px 6px 7px' }}>
          <span className="flex-1 min-w-0">
            <span className="block truncate" style={ESTILO_NOME}>
              {nomeDoArquivo(item)}
            </span>
            <span className="block truncate" style={ESTILO_META}>
              {`${formatFileSize(item.size)} · ${formatShortDate(item.sentAt)}`}
            </span>
          </span>
          {botaoBaixar(item, indice)}
        </div>
      </div>
    );
  };

  const emGrade = EM_GRADE.has(aba);

  let conteudo: React.ReactNode;
  if (isLoading) {
    conteudo = (
      <p className="cdp-empty" data-testid="details-files-loading">
        Carregando arquivos…
      </p>
    );
  } else if (visiveis.length === 0) {
    conteudo = (
      <p className="cdp-empty" data-testid="details-files-empty">
        {termo === ''
          ? 'Nenhum arquivo trocado nesta conversa.'
          : 'Nenhum arquivo com esse nome.'}
      </p>
    );
  } else if (emGrade) {
    conteudo = (
      <div className="grid grid-cols-2" style={{ gap: 8, paddingTop: 8 }}>
        {visiveis.map(celula)}
      </div>
    );
  } else {
    conteudo = visiveis.map(linha);
  }

  return (
    <div className="flex flex-col">
      <div className="mb-2.5">
        <Input
          icon="search"
          value={busca}
          onChange={(evento) => {
            setBusca(evento.target.value);
            setImagemAberta(null);
          }}
          placeholder="Buscar pelo nome"
          aria-label="Buscar arquivo pelo nome"
          data-testid="details-files-search"
        />
      </div>

      {/* Não existe classe `.cdp-*` para as sub-abas: o estilo vem do desenho
          aprovado (`.v18-ftab`) escrito aqui com os tokens do tema. */}
      <div
        className="flex flex-wrap"
        style={{
          gap: 5,
          paddingBottom: 9,
          marginBottom: 2,
          borderBottom: '1px solid var(--theme-sidebar-border)',
        }}
      >
        {ABAS.map(({ id, label, icone }) => {
          const ativo = id === aba;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={ativo}
              onClick={() => {
                setAba(id);
                setImagemAberta(null);
              }}
              className="inline-flex items-center transition-colors"
              style={{
                gap: 5,
                height: 28,
                padding: '0 9px',
                borderRadius: 9999,
                fontSize: 11,
                fontWeight: 600,
                border: '1px solid',
                borderColor: ativo
                  ? 'color-mix(in srgb, var(--theme-primary) 32%, transparent)'
                  : 'var(--theme-sidebar-border)',
                background: ativo
                  ? 'color-mix(in srgb, var(--theme-primary) 14%, var(--theme-sidebar-background))'
                  : 'var(--theme-surface-sunken)',
                color: ativo ? 'var(--theme-primary)' : 'var(--theme-muted-text)',
              }}
              data-testid={`details-files-tab-${id}`}
            >
              {icone ? <Icon name={icone} className="w-3 h-3" /> : null}
              {label}
              <b style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {contagemDaAba(id, data.mediaCounts)}
              </b>
            </button>
          );
        })}
      </div>

      <div data-testid="details-files-list">{conteudo}</div>

      {/* Sentinela da rolagem infinita: entrar em tela é o que pede a próxima
          página. Fica fora da lista para não virar item dela. */}
      {mostrarSentinela && <div ref={sentinelaRef} aria-hidden="true" />}

      {isFetchingNextPage && (
        <p
          className="cdp-empty"
          style={{ padding: '10px 8px' }}
          data-testid="details-files-loading-more"
        >
          Carregando mais arquivos…
        </p>
      )}

      <ImageViewer
        images={imagens.map((item) => ({
          id: item.messageId,
          url: item.url,
          sentAt: item.sentAt,
          ...(item.caption ? { caption: item.caption } : {}),
        }))}
        initialIndex={imagemAberta ?? 0}
        isOpen={imagemAberta !== null}
        onClose={() => setImagemAberta(null)}
      />
    </div>
  );
};

export default FilesLayer;
